import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHead from "@/components/PageHead";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/hooks/use-locale";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { trackCTAClick } from "@/lib/analytics";
import { Headphones, Moon, BookOpen } from "lucide-react";
import JsonLd from "@/components/JsonLd";
import FaqSection, { toFaqItems } from "@/components/FaqSection";
import { audioBookSchema, breadcrumbSchema } from "@/lib/seo-schemas";

const NORMAL_URL = "https://soundcloud.com/queebi-53717989/good-night-animal-friends-on-queebi-land-normal-speed";
const SLOW_URL = "https://soundcloud.com/queebi-53717989/good-night-animal-friends-on-queebi-land";

const buildEmbed = (trackUrl: string) =>
  `https://w.soundcloud.com/player/?url=${encodeURIComponent(trackUrl)}&color=%23E8A838&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`;

const Audiobook = () => {
  const { t } = useTranslation();
  const { prefix } = useLocale();
  const [heroRef, heroVisible] = useScrollAnimation<HTMLElement>();

  const audiobookFaq = toFaqItems(t("faq.audiobook", { returnObjects: true }));
  const audiobookLd = audioBookSchema({
    name: "Good Night, Animals on Queebi Land",
    description: "A cozy bedtime story for little ones, featuring the six Queebi Friends. Available in normal and slow read-along versions.",
    url: "https://queebi.com/audiobook",
    image: "/images/brand/og-image.png",
    inLanguage: "en",
  });
  const breadcrumb = breadcrumbSchema([
    { name: "Home", url: "https://queebi.com/" },
    { name: "Audiobook", url: "https://queebi.com/audiobook" },
  ]);

  return (
    <>
      <PageHead
        title={t("audiobook.seoTitle")}
        description={t("audiobook.seoDesc")}
        canonical="https://queebi.com/audiobook"
        image="/images/og/audiobook-1200x630.jpg"
        imageAlt="The Queebi Land Audiobook — bedtime stories that grow with your child"
        keywords="Queebi audiobook, bedtime story, Good Night Animals, kids audiobook"
      />
      <JsonLd data={[audiobookLd, breadcrumb]} />

      <section
        ref={heroRef}
        className={`bg-gradient-to-b from-secondary/40 to-background py-20 transition-all duration-700 ${heroVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
      >
        <div className="container max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/15 text-accent font-bold text-sm mb-6">
            <Headphones className="h-4 w-4" />
            {t("audiobook.badge")}
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4 text-foreground">
            {t("audiobook.title")}
          </h1>
          <p className="text-lg text-foreground/70 max-w-2xl mx-auto">
            {t("audiobook.subtitle")}
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container max-w-3xl">
          <div className="bg-card rounded-3xl p-8 md:p-10 shadow-sm border border-border/50 mb-12">
            <h2 className="font-heading text-2xl font-bold mb-4 text-foreground">
              {t("audiobook.howToTitle")}
            </h2>
            <p className="text-foreground/80 mb-4">{t("audiobook.howToIntro")}</p>
            <ul className="space-y-3 text-foreground/80">
              <li className="flex gap-3">
                <Moon className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <span><strong>{t("audiobook.normalLabel")}</strong> — {t("audiobook.normalDesc")}</span>
              </li>
              <li className="flex gap-3">
                <BookOpen className="h-5 w-5 text-accent shrink-0 mt-0.5" />
                <span><strong>{t("audiobook.slowLabel")}</strong> — {t("audiobook.slowDesc")}</span>
              </li>
            </ul>
          </div>

          <div className="space-y-10">
            <div>
              <h3 className="font-heading text-xl font-bold mb-3 text-foreground flex items-center gap-2">
                <Moon className="h-5 w-5 text-accent" />
                {t("audiobook.normalTitle")}
              </h3>
              <div
                className="rounded-2xl overflow-hidden shadow-md border border-border/50"
                onClick={() => trackCTAClick("audiobook_play_normal")}
              >
                <iframe
                  width="100%"
                  height="180"
                  scrolling="no"
                  frameBorder="no"
                  allow="autoplay"
                  src={buildEmbed(NORMAL_URL)}
                  title="Good Night Animal Friends — Normal Speed"
                />
              </div>
            </div>

            <div>
              <h3 className="font-heading text-xl font-bold mb-3 text-foreground flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-accent" />
                {t("audiobook.slowTitle")}
              </h3>
              <div
                className="rounded-2xl overflow-hidden shadow-md border border-border/50"
                onClick={() => trackCTAClick("audiobook_play_slow")}
              >
                <iframe
                  width="100%"
                  height="180"
                  scrolling="no"
                  frameBorder="no"
                  allow="autoplay"
                  src={buildEmbed(SLOW_URL)}
                  title="Good Night Animal Friends — Slow Speed"
                />
              </div>
            </div>
          </div>

          <div className="mt-14 text-center">
            <Button
              asChild
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold transition-transform hover:scale-105"
            >
              <Link
                to={`${prefix}/queebi-land-characters`}
                onClick={() => trackCTAClick("audiobook_meet_characters")}
              >
                {t("audiobook.meetCharactersCta")}
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <FaqSection title={t("faq.title")} items={audiobookFaq} />
    </>
  );
};

export default Audiobook;
